import { useState } from 'react';
import {
  CheckSquare,
  Calendar,
  CalendarDays,
  User,
  Tag,
  Crosshair,
  FileText,
  LayoutGrid,
  Inbox,
  StickyNote,
  X,
  type LucideIcon,
} from 'lucide-react';

export type EntityType =
  | 'task'
  | 'meeting_instance'
  | 'meeting_series'
  | 'reference_doc'
  | 'person'
  | 'tag'
  | 'scope'
  | 'quick_capture'
  | 'note';

const ENTITY_CONFIG: Record<EntityType, { icon: LucideIcon; color: string; bg: string }> = {
  task:              { icon: CheckSquare,  color: '#6366f1', bg: '#6366f120' },
  meeting_instance:  { icon: Calendar,     color: '#f59e0b', bg: '#f59e0b20' },
  meeting_series:    { icon: CalendarDays, color: '#f59e0b', bg: '#f59e0b20' },
  reference_doc:     { icon: LayoutGrid,   color: '#22c55e', bg: '#22c55e20' },
  person:            { icon: User,         color: '#3b82f6', bg: '#3b82f620' },
  tag:               { icon: Tag,          color: '#a855f7', bg: '#a855f720' },
  scope:             { icon: Crosshair,    color: '#ec4899', bg: '#ec489920' },
  quick_capture:     { icon: Inbox,        color: '#64748b', bg: '#64748b20' },
  note:              { icon: StickyNote,   color: '#14b8a6', bg: '#14b8a620' },
};

interface EntityChipProps {
  entityType: EntityType;
  name: string;
  color?: string;
  onRemove?: () => void;
  onClick?: () => void;
  size?: 'sm' | 'md';
  snippet?: string;
  exists?: boolean;
}

export function EntityChip({ entityType, name, color, onRemove, onClick, size = 'sm', snippet, exists = true }: EntityChipProps) {
  const config = ENTITY_CONFIG[entityType];
  const Icon = config.icon;
  const [hover, setHover] = useState(false);

  const dead = !exists;
  const clickable = !!onClick && !dead;
  const chipColor = dead ? '#636b88' : (color ?? config.color);
  const chipBg = dead ? '#636b8820' : (color ? color + '20' : config.bg);

  const sizeClasses = size === 'sm'
    ? 'text-xs px-2 py-0.5 gap-1'
    : 'text-sm px-2.5 py-1 gap-1.5';

  return (
    <span
      className={`relative inline-flex items-center rounded-full ${sizeClasses} ${
        clickable ? 'cursor-pointer hover:opacity-80' : dead ? 'cursor-default line-through' : ''
      }`}
      style={{ backgroundColor: chipBg, color: chipColor }}
      onClick={clickable ? onClick : undefined}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      title={dead ? 'This item was deleted' : undefined}
    >
      <Icon size={size === 'sm' ? 10 : 12} />
      <span className="truncate max-w-[150px]">{name}</span>
      {onRemove && (
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="hover:opacity-70 flex-shrink-0"
        >
          <X size={size === 'sm' ? 10 : 12} />
        </button>
      )}
      {hover && !dead && (name.length > 18 || snippet) && (
        <span className="entity-chip-popover">
          <span className="entity-chip-popover-type">{formatType(entityType)}</span>
          <span className="entity-chip-popover-title">{name}</span>
          {snippet && <span className="entity-chip-popover-snippet">{snippet}</span>}
        </span>
      )}
    </span>
  );
}

function formatType(t: EntityType): string {
  return t.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export function getEntityIcon(entityType: EntityType): LucideIcon {
  return ENTITY_CONFIG[entityType]?.icon ?? FileText;
}

export function getEntityColor(entityType: EntityType): string {
  return ENTITY_CONFIG[entityType]?.color ?? '#6366f1';
}
